import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, MapPin, ChevronLeft, ChevronRight, Ticket, Clock } from 'lucide-react';
import api, { eventsApi } from '../lib/api';
import { formatEventDate } from '../lib/dateUtils';
import EventPromoBadge from './EventPromoBadge';

/**
 * Home Hero Slider.
 * Pulls from the admin-managed "Banner de Eventos Destacados" (same as /eventos).
 * Falls back to latest published events with image.
 * Each slide is fully clickable, with producer info, date and a "Comprar" CTA.
 */
const HomeEventCarousel = () => {
  const [events, setEvents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const featuredRes = await eventsApi.getFeaturedEvents().catch(() => ({ data: [] }));
        const featured = (featuredRes.data || []).filter(e => e && (e.image_url || e.cover_image));
        if (featured.length > 0) {
          if (mounted) setEvents(featured);
          return;
        }
        const fallback = await api.get('/events/?include_past=false').catch(() => ({ data: [] }));
        const list = (fallback.data || [])
          .filter(e => (e.status === 'published' || !e.status) && (e.image_url || e.cover_image))
          .slice(0, 5);
        if (mounted) setEvents(list);
      } catch (_) {
        // silent
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!isAutoPlaying || events.length <= 1) return;
    const t = setInterval(() => setCurrentIndex((p) => (p + 1) % events.length), 6000);
    return () => clearInterval(t);
  }, [isAutoPlaying, events.length]);

  const goToPrev = useCallback(() => {
    setIsAutoPlaying(false);
    setCurrentIndex((p) => (p - 1 + events.length) % events.length);
  }, [events.length]);

  const goToNext = useCallback(() => {
    setIsAutoPlaying(false);
    setCurrentIndex((p) => (p + 1) % events.length);
  }, [events.length]);

  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) (diff > 0 ? goToNext : goToPrev)();
    setTouchStart(null);
  };

  if (events.length === 0) {
    return (
      <div
        className="relative w-full aspect-[3/4] sm:aspect-[16/10] lg:aspect-[21/9] bg-gradient-to-br from-violet-900/20 via-[#0A0A0A] to-pink-900/10 animate-pulse"
        data-testid="home-event-carousel-loading"
      />
    );
  }

  const currentEvent = events[currentIndex];
  const nextEvent = events[(currentIndex + 1) % events.length];

  return (
    <div
      className="relative w-full"
      data-testid="home-event-carousel"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative aspect-[3/4] sm:aspect-[16/10] lg:aspect-[21/9] overflow-hidden bg-black">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0"
          >
            <img
              src={currentEvent.image_url || currentEvent.cover_image}
              alt={currentEvent.title}
              className={`w-full h-full object-cover ${
                currentEvent.image_position === 'top' ? 'object-top'
                : currentEvent.image_position === 'bottom' ? 'object-bottom'
                : 'object-center'
              }`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/75 via-transparent to-transparent" />
          </motion.div>
        </AnimatePresence>

        {/* Slide content */}
        <div className="absolute inset-0 flex items-end pb-6 sm:pb-0 sm:items-center">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:pr-72">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="max-w-xl"
              >
                {(currentEvent.producer_name || currentEvent.producer_id) && (
                  <Link
                    to={currentEvent.producer_id ? `/productora/${currentEvent.producer_slug || currentEvent.producer_id}` : '#'}
                    className="inline-flex items-center gap-2.5 px-3 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 hover:border-[#D4AF37]/50 hover:bg-white/15 transition-all mb-3 group"
                    data-testid="home-hero-producer-badge"
                  >
                    {currentEvent.producer_logo ? (
                      <img
                        src={currentEvent.producer_logo}
                        alt={currentEvent.producer_name || 'Productora'}
                        className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover border border-white/30"
                      />
                    ) : (
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#D4AF37]/30 border border-[#D4AF37]/50 flex items-center justify-center">
                        <span className="text-xs sm:text-sm font-bold text-[#D4AF37]">
                          {currentEvent.producer_name?.charAt(0) || 'P'}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] sm:text-xs text-white/50 uppercase tracking-wider font-semibold">Presenta</span>
                      <span className="text-sm sm:text-base text-white font-bold group-hover:text-[#D4AF37] transition-colors">
                        {currentEvent.producer_name || 'Productora'}
                      </span>
                    </div>
                  </Link>
                )}

                {currentEvent.date && (
                  <div className="mb-4 flex items-center gap-2 flex-wrap">
                    <div className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-3 py-1.5 rounded-sm">
                      <Calendar size={14} />
                      <span className="font-mono font-bold text-xs uppercase tracking-wider">
                        {formatEventDate(currentEvent.date, 'EEE d MMM')}
                      </span>
                    </div>
                    <EventPromoBadge promo={currentEvent.active_promo} />
                  </div>
                )}

                <Link to={`/evento/${currentEvent.slug || currentEvent.id}`}>
                  <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white mb-2 hover:text-[#D4AF37] transition-colors leading-none">
                    {currentEvent.title}
                  </h2>
                </Link>

                <div className="flex flex-wrap items-center gap-3 text-white/70 text-sm mb-4">
                  {currentEvent.time && (
                    <div className="flex items-center gap-1.5">
                      <Clock size={14} className="text-[#D4AF37]" />
                      <span className="font-mono">{currentEvent.time}hs</span>
                    </div>
                  )}
                  {(currentEvent.venue_name || currentEvent.location) && (
                    <div className="flex items-center gap-1.5">
                      <MapPin size={14} className="text-[#D4AF37]" />
                      <span className="truncate max-w-[150px] sm:max-w-[250px]">{currentEvent.venue_name || currentEvent.location}</span>
                    </div>
                  )}
                </div>

                <Link
                  to={`/evento/${currentEvent.slug || currentEvent.id}`}
                  data-testid={`home-hero-cta-${currentEvent.id}`}
                  className="inline-flex items-center gap-2 bg-white text-black px-4 py-2.5 sm:px-6 sm:py-3 font-bold uppercase tracking-wider text-xs sm:text-sm hover:bg-[#D4AF37] transition-all duration-300"
                >
                  <Ticket size={16} />
                  <span>Comprar</span>
                </Link>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Tablet arrows */}
        {events.length > 1 && (
          <>
            <button
              onClick={goToPrev}
              data-testid="home-hero-prev-btn"
              className="hidden sm:flex lg:hidden absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-black/60 hover:bg-black/90 border border-white/20 hover:border-[#D4AF37] items-center justify-center text-white hover:text-[#D4AF37] transition-all z-20"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              onClick={goToNext}
              data-testid="home-hero-next-btn"
              className="hidden sm:flex lg:hidden absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-black/60 hover:bg-black/90 border border-white/20 hover:border-[#D4AF37] items-center justify-center text-white hover:text-[#D4AF37] transition-all z-20"
            >
              <ChevronRight size={22} />
            </button>
          </>
        )}

        {/* Desktop next preview */}
        {events.length > 1 && (
          <div className="hidden lg:block absolute right-6 top-1/2 -translate-y-1/2 w-48 z-10">
            <div
              className="relative aspect-[3/4] overflow-hidden border-2 border-white/20 hover:border-[#D4AF37] cursor-pointer transition-all duration-300 group bg-black"
              onClick={goToNext}
              data-testid="home-hero-next-preview"
            >
              <img
                src={nextEvent.image_url || nextEvent.cover_image}
                alt={nextEvent.title}
                className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <span className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-wider">Siguiente</span>
                <p className="text-sm font-bold text-white line-clamp-2 mt-1 leading-tight">
                  {nextEvent.title}
                </p>
                {nextEvent.date && (
                  <p className="text-xs text-white/60 font-mono mt-1">
                    {formatEventDate(nextEvent.date, 'd MMM')}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Indicators */}
        {events.length > 1 && (
          <div className="absolute bottom-3 sm:bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
            {events.map((_, index) => (
              <button
                key={index}
                onClick={() => { setIsAutoPlaying(false); setCurrentIndex(index); }}
                data-testid={`home-hero-indicator-${index}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'bg-[#D4AF37] w-6' : 'bg-white/40 hover:bg-white/60 w-1.5'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeEventCarousel;
